import React, { useMemo, useState, useEffect, useLayoutEffect, useRef } from 'react'
import MultiChoiceOption from "components/challenges/multiChoice/MultiChoiceOption"
import PopupTooltip from "components/PopupTooltip"
import ChallengeTag from "components/ChallengeTag"

import * as lessonParser from "utils/lessonParser"

import AIExplanation, { AIExplainIcon } from "components/AIExplanation"

function shuffleArray(array) {
    const shuffled = array.slice();
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
}

function MultiChoice({ questionData, questionVariables, questionText, questionStatus, onAnswerCheck, onHelpUsed, tag }) {
    const aiRef = useRef();
    const lessonScrollRef = useRef(null);
    const [selected, setSelected] = useState(null);
    const popupRef = useRef(null);

    const choices = useMemo(() => {
        if (!questionData?.data) return [];

        const correctChoices = lessonParser
            .parseValueList(questionData.data.choices?.correct, false, questionVariables)
            .map(choice => ({ ...choice, answer: true }));

        const incorrectChoices = lessonParser
            .parseValueList(
                questionData.data.choices?.incorrect,
                false,
                questionVariables,
                correctChoices[0]?.value
            )
            .map(choice => ({ ...choice, answer: false }));

        const allChoices = [...correctChoices, ...incorrectChoices];
        // console.log("Choices:", allChoices)
        return shuffleArray(allChoices);
    }, [JSON.stringify(questionData?.data?.choices), questionVariables]);

    const handleHelpClick = () => {
        // Hide the popup by marking it as seen
        if (popupRef.current) {
            popupRef.current.handleInteraction();
        }

        // Notify parent that help was used
        onHelpUsed?.();

        // Trigger AI explanation
        aiRef.current?.getAIExplanation(questionData, {
            question: questionText,
            choices: choices,
            selected: selected
        });
        aiRef.current?.showSection();
        setTimeout(() => {
            if (lessonScrollRef.current) {
                lessonScrollRef.current.scrollTo({
                    top: lessonScrollRef.current.scrollHeight,
                    behavior: 'smooth'
                });
            }
        }, 200);
    };

    useEffect(() => {
        onAnswerCheck((force) => {
            // console.log("Checking answer for MultiChoice:", force);
            if (selected === null && !force) return null

            setTimeout(() => {
                if (lessonScrollRef.current) {
                    lessonScrollRef.current.scrollTo({
                        top: lessonScrollRef.current.scrollHeight,
                        behavior: 'smooth'
                    });
                }
            }, 10);
            let isCorrect = !!choices[selected]?.answer;
            if (!isCorrect && !force) {
                requestAnimationFrame(() => {
                    aiRef.current?.getAIExplanation(questionData, {
                        question: questionText,
                        choices: choices,
                        selected: selected
                    });
                });
            }
            return { isCorrect: isCorrect, message: choices.find(c => c.answer)?.value };
        });
    }, [onAnswerCheck, selected, choices]);

    return (
        <div ref={lessonScrollRef} className='text-text flex justify-center w-full overflow-auto max-h-full pb-2 pt-12 break-small:pt-2'>
            <div className='flex items-center flex-col w-full h-full break-small:w-min '>
                <div className="flex justify-center w-full">
                    <div className='px-10 break-small:px-0 w-full break-big:w-full text-center break-small:text-left'>
                        {tag && <div className='mb-3'><ChallengeTag tag={tag} /></div>}
                        <h1 className='font-bold text-[5vw] break-small:text-5xl mb-[4vh] break-big:mb-[5vh]'>{questionText}</h1>
                    </div>
                </div>
                <div className="flex flex-col items-center w-full">
                    <div className='Choices max-w-[90vw] w-[500px] break-small:w-[800px]  break-big:w-[1000px] grid gap-3 grid-cols-1 break-small:grid-cols-[repeat(2,1fr)] place-items-center pb-8'>
                        {choices.map((choice, optionIndex) =>
                            // <h1>{choice.data}</h1>
                            <MultiChoiceOption text={choice.value} optionIndex={optionIndex} questionStatus={questionStatus} selected={selected} setSelected={setSelected} key={choice.value} options={questionData.data?.options} />
                        )}
                    </div>
                    {(questionStatus["guessed"] && !questionStatus["answer"]) && <div className='max-w-[90vw] w-full break-small:w-[800px]  break-big:w-[1000px] min-h-40 rounded-xl bg-warning-darker border-warning border-[3px] flex p-6 px-6  flex-col gap-5 mb-8 ' style={{ animation: "incorrectSection 450ms ease" }}>
                        <div className='flex flex-col sm:flex-row items-center justify-between'>
                            <h1 className='text-text text-2xl font-bold text-left'>Correct Answer:</h1>
                            <button
                                className='pr-1 text-primary text-lg font-bold text-right flex items-center gap-2 outline-none hover:underline relative'
                                onClick={handleHelpClick}
                            ><AIExplainIcon /> See how to get this answer
                                <PopupTooltip
                                    ref={popupRef}
                                    text="Stuck? Use free AI to help you understand it better!"
                                    open={questionStatus["guessed"] && !questionStatus["answer"]}
                                    storageKey="seenHelpPopup"
                                    autoMarkSeenAfter={5000}
                                />
                            </button>
                        </div>

                        <MultiChoiceOption text={choices.find(c => c.answer)?.value} optionIndex={0} questionStatus={{ "guessed": true }} selected={1} setSelected={setSelected} key={"Incorrect Display " + choices.find(c => c.answer)?.value} options={questionData.data?.options} />
                        <AIExplanation ref={aiRef} />
                    </div>}
                </div>
            </div>
        </div>
    )
}

export default MultiChoice